import {
  HomeyAPI,
  type HomeyAPIV2,
  type LedringScreensaver,
  type LedringScreensaverReference,
} from 'homey-api';
import { shallow } from 'zustand/shallow';

import { hasStatusCode } from '../../../lib/errors';

import { BaseApiStore, type FetchArgs } from '../../../store/BaseApiStore.ts';
import { selectBase } from '../../../store/selectors.ts';
import { useAttach } from '../../../store/useAttach';

type BrightnessOption = Awaited<ReturnType<HomeyAPIV2.ManagerLedring['getOptionBrightness']>>;

export interface State {
  screensaversData: LedringScreensaver[] | null;
  optionScreensaverData: { value: LedringScreensaverReference } | null;
  optionBrightnessData: BrightnessOption | null;
  isLoading: boolean;
  error: unknown;
}

export type LedringSettingsStoreState = State;

export class LedringSettingsStore extends BaseApiStore<State> {
  constructor() {
    super('ledring-settings');
  }

  override createInitialState() {
    return {
      screensaversData: null,
      optionScreensaverData: null,
      optionBrightnessData: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    if (HomeyAPI.isHomeyAPIV3Cloud(api) === true) {
      this.set({ ...this.createInitialState(), isLoading: false });
      return;
    }

    try {
      const screensaversPromise = api.ledring.getScreensavers();
      const optionScreensaverPromise = api.ledring.getOptionScreensaver();
      const optionBrightnessPromise = LedringSettingsStore.getOptionBrightness(api);

      const [screensaversData, optionScreensaverData, optionBrightnessData] = await Promise.all([
        screensaversPromise,
        optionScreensaverPromise,
        optionBrightnessPromise,
      ]);

      if (abortController.signal.aborted) {
        return;
      }

      const sortedScreensaversData = [...screensaversData].sort((a, b) => {
        if (a.id === b.id) {
          return 0;
        }

        if (a.id === 'off') {
          return 1;
        }

        if (b.id === 'off') {
          return -1;
        }

        return 0;
      });

      this.set({
        ...this.createInitialState(),
        screensaversData: sortedScreensaversData,
        optionScreensaverData,
        optionBrightnessData,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy() {}

  override onAttached({ api }: { api: HomeyAPI }): void {}

  override onDetached({ api }: { api: HomeyAPI }): void {}

  use() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAttach(this, 'ledring-settings');

    return { ledringSettings: this.store(selectAll, shallow) };
  }

  async setOptionScreensaver({ value }: { value: LedringScreensaverReference }) {
    const api = this.getApi();

    if (HomeyAPI.isHomeyAPIV3Cloud(api) === true) {
      throw new Error('LED ring settings are not supported on Homey Cloud');
    }

    const result = await api.ledring.setOptionScreensaver({ value: { ...value } });
    this.set({ optionScreensaverData: result });
  }

  async setOptionBrightness({ value }: BrightnessOption) {
    const api = this.getApi();

    if (HomeyAPI.isHomeyAPIV2(api) === false) {
      throw new Error('LED ring brightness is only supported on Homey API V2');
    }

    const result = await api.ledring.setOptionBrightness({ value });
    this.set({ optionBrightnessData: result });
  }

  static async getOptionBrightness(api: HomeyAPI): Promise<BrightnessOption | null> {
    if (HomeyAPI.isHomeyAPIV2(api) === false) {
      return null;
    }

    try {
      return await api.ledring.getOptionBrightness();
    } catch (error) {
      if (hasStatusCode(error) && error.statusCode === 404) {
        return null;
      }

      if (typeof error === 'object' && error !== null && 'code' in error && error.code === 404) {
        return null;
      }

      throw error;
    }
  }
}

function selectAll(state: LedringSettingsStoreState) {
  return {
    ...selectBase(state),
    screensaversData: state.screensaversData,
    optionScreensaverData: state.optionScreensaverData,
    optionBrightnessData: state.optionBrightnessData,
  };
}

export const ledringSettingsStore = new LedringSettingsStore();
